import { Center } from "@chakra-ui/react";
import { FieldProps } from "@rjsf/utils";
import { bbox } from "@turf/turf";
import { useContext } from "react";
import { FormattedMessage } from "react-intl";
import { Popup } from "react-map-gl/maplibre";

import { RanaButton, RanaHeading, SIZES, TrashIcon } from "@nens/ranalib";
import { NETHERLANDS_EXTENT } from "@nens/shared-utils";

import { RanaMap } from "@/lib/rana-map";
import { ProcessRunFormMapViewStateContext } from "@/stores/process-run-form-map-view-state-provider";
import { ProjectContext } from "@/stores/project-provider";

import { ModeButton } from "./components/process-run-form-draw-geometry-mode-button";
import { useProcessRunFormDrawGeometry } from "./hooks/use-process-run-form-draw-geometry";
import { DrawGeometryFormData } from "./types/draw-geometry-form-data";

export function ProcessRunFormDrawGeometry(props: FieldProps<DrawGeometryFormData>) {
  const { disabled, readonly, formData } = props;
  const project = useContext(ProjectContext);
  const processRunFormHook = useProcessRunFormDrawGeometry(props);
  const {
    onMapClick,
    onMapMouseMove,
    getMapCursor,
    layers,
    removePopup,
    setRemovePopup,
    isDeleting,
    setIsDeleting,
    somethingOnMap,
    modes,
    modeAllowsMultiple,
  } = processRunFormHook;

  const { mapViewState, setMapView } = useContext(ProcessRunFormMapViewStateContext);

  const showModesButtons = modes.length > 1;

  const formDataViewState =
    formData && formData.coordinates.length
      ? { bounds: bbox(formData) as [number, number, number, number] }
      : undefined;

  const projectLocationViewState = project.location
    ? {
        longitude: project.location.coordinates[0],
        latitude: project.location.coordinates[1],
        zoom: 11,
      }
    : undefined;

  const initialViewState = mapViewState ?? formDataViewState ?? projectLocationViewState;

  return (
    <RanaMap
      mapId={props.name}
      containerProps={{ h: "md" }}
      // `bounds` serves two purposes in RanaMap:
      //   1. As `initialViewState.bounds` — positions the map on first mount (one-time, no animation).
      //   2. Via `useMapBounds` — reactively calls `map.fitBounds(...)` with animation whenever bounds change.
      //
      // When `initialViewState` is available (project location or saved view state), we pass it via
      // `mapProps.initialViewState` instead, so `bounds` is not needed for initial positioning → pass undefined.
      //
      // When there is no `initialViewState` (no project location, no saved view), we fall back to
      // `NETHERLANDS_EXTENT` via `bounds` so the map is positioned correctly on mount.
      //
      // `dontApplyInitialBounds` is always true here to suppress the reactive `fitBounds` call on mount.
      // Without it, `useMapBounds` would fire on mount and animate to `NETHERLANDS_EXTENT` even though
      // the map is already positioned there via `initialViewState.bounds` — causing a redundant animation.
      // On subsequent `bounds` changes (which never happen here since bounds is static), it would still fire.
      bounds={initialViewState ? undefined : NETHERLANDS_EXTENT}
      dontApplyInitialBounds
      mapProps={{
        onMouseMove: onMapMouseMove,
        onClick: onMapClick,
        onMoveEnd: setMapView,
        ...(initialViewState ? { initialViewState } : {}),
      }}
      deckGlProps={{
        layers,
        getCursor: getMapCursor,
      }}
    >
      <RanaMap.MapScale />

      {!disabled && !readonly && (
        <RanaMap.ControlsBottom overflow="visible">
          {(showModesButtons ? modes : []).map((buttonMode, index) => (
            <ModeButton
              {...processRunFormHook}
              key={buttonMode}
              buttonMode={buttonMode}
              index={index}
            />
          ))}
          {/* Dont bother showing the delete button when there can't be multiple */}
          {modeAllowsMultiple && (
            <RanaMap.MapButton
              p={2}
              roundR
              onClick={() => setIsDeleting(!isDeleting)}
              {...(showModesButtons
                ? {
                    position: "absolute",
                    left: "full",
                    transform: `translateX(${somethingOnMap ? 0 : -100}%)`,
                  }
                : { roundL: true, isDisabled: !somethingOnMap })}
              {...(isDeleting ? { isActive: true } : { bg: "white" })}
              _active={{ bg: "semantic.error.main" }}
            >
              <TrashIcon />
            </RanaMap.MapButton>
          )}
        </RanaMap.ControlsBottom>
      )}
      {removePopup ? (
        <Popup
          anchor="left"
          longitude={removePopup.coordinates[0]}
          latitude={removePopup.coordinates[1]}
          onClose={() => setRemovePopup(null)}
          closeOnClick={false}
          style={{ maxHeight: SIZES["2xs"], zIndex: "9999", marginLeft: SIZES[4] }}
        >
          <Center flexDirection="column">
            <RanaHeading type="h6" m={3}>
              <FormattedMessage id="process.remove_this" defaultMessage="Remove this" />{" "}
              {removePopup.type}?
            </RanaHeading>

            <RanaButton
              mx="auto"
              h="small_button"
              variant="primary"
              onClick={() => {
                removePopup.onConfirm();
                setRemovePopup(null);
              }}
            >
              <FormattedMessage id="process.remove" defaultMessage="Remove" />
            </RanaButton>
          </Center>
        </Popup>
      ) : null}
    </RanaMap>
  );
}
